import { useState, useEffect } from "react";
import { adminGetApplications, adminReviewApp, adminGetDocumentUrl } from "@/api";
import { toast } from "sonner";
import { FileText, Search, CheckCircle, XCircle, Loader2, Eye, Clock, IndianRupee, User, Calendar, X, Download, ExternalLink, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/config/navigationConfig";

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'disbursed', label: 'Disbursed' },
];

export default function AdminApplications() {
  const { user } = useAuth();
  const canApprove = canWrite(user, 'approve_applications');
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState('');

  useEffect(() => { loadApps(); }, [filter]);

  const loadApps = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const res = await adminGetApplications(params);
      setApps(res.data.applications || []);
    } catch (err) {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  };

  const handleReview = async (action) => {
    if (!selectedApp) return;
    setActionLoading(true);
    try {
      await adminReviewApp(selectedApp.id, action, remarks);
      toast.success(`Application ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setSelectedApp(null);
      setRemarks('');
      loadApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} application`);
    } finally { setActionLoading(false); }
  };

  const openDoc = async (docId) => {
    try {
      const res = await adminGetDocumentUrl(docId);
      window.open(res.data.signed_url, '_blank');
    } catch (err) {
      toast.error("Failed to get document URL");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      disbursed: "bg-blue-100 text-blue-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const filteredApps = apps.filter(app => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (app.application_number || '').toLowerCase().includes(s) ||
      (app.user?.name || '').toLowerCase().includes(s) ||
      (app.user?.mobile || '').includes(s);
  });

  return (
    <div className="fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Applications</h1>
          <p className="text-sm text-gray-500 mt-1">Review and manage loan assistance applications</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredApps.length} application(s)</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, mobile, app #..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${filter === f.value ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Applications Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1565C0]" /></div>
      ) : filteredApps.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No applications found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">App #</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Tenure</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{app.application_number || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0A2540]">{app.user?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-400">{app.user?.mobile || ''}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">₹{parseFloat(app.amount_requested || app.loan_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">{app.tenure || '-'} mo</td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(app.status)}`}>
                        {app.status?.charAt(0).toUpperCase() + app.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : ''}</td>
                    <td className="px-4 py-3">
                      <Button onClick={() => { setSelectedApp(app); setRemarks(''); }} variant="ghost" size="sm" className="text-[#1565C0]">
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="font-bold text-lg text-[#0A2540]">Application Details</h2>
                <span className="text-xs font-mono text-gray-400">{selectedApp.application_number || 'N/A'}</span>
              </div>
              <button onClick={() => setSelectedApp(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><User className="h-4 w-4" /> Customer Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                  <div><span className="text-xs text-gray-500">Name</span><p className="font-semibold text-sm">{selectedApp.user?.name || 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">Mobile</span><p className="font-semibold text-sm">{selectedApp.user?.mobile || 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">KYC</span><p className="font-semibold text-sm">{selectedApp.user?.kyc_status || 'pending'}</p></div>
                  <div><span className="text-xs text-gray-500">PAN</span><p className="font-semibold text-sm">{selectedApp.user?.pan_verified ? 'Verified' : 'Not Verified'}</p></div>
                  <div><span className="text-xs text-gray-500">Employment</span><p className="font-semibold text-sm">{(selectedApp.user?.employment_type || '').replace('_', ' ') || 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">Income</span><p className="font-semibold text-sm">₹{parseFloat(selectedApp.user?.monthly_income || 0).toLocaleString()}/mo</p></div>
                </div>
              </div>

              {/* Loan Details */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><IndianRupee className="h-4 w-4" /> Loan Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-blue-50 rounded-xl p-4">
                  <div><span className="text-xs text-gray-500">Requested Amount</span><p className="font-bold text-lg text-[#1565C0]">₹{parseFloat(selectedApp.amount_requested || selectedApp.loan_amount || 0).toLocaleString()}</p></div>
                  <div><span className="text-xs text-gray-500">Tenure</span><p className="font-bold text-lg">{selectedApp.tenure || '-'} months</p></div>
                  <div><span className="text-xs text-gray-500">Interest Rate</span><p className="font-semibold text-sm">{selectedApp.interest_rate || '-'}%/mo</p></div>
                  <div><span className="text-xs text-gray-500">Total Repayable</span><p className="font-semibold text-sm">₹{parseFloat(selectedApp.total_repayable || 0).toLocaleString()}</p></div>
                  <div><span className="text-xs text-gray-500">Monthly EMI</span><p className="font-semibold text-sm">₹{parseFloat(selectedApp.emi_amount || 0).toLocaleString()}</p></div>
                  <div><span className="text-xs text-gray-500">Purpose</span><p className="font-semibold text-sm">{selectedApp.purpose || 'EMI Assistance'}</p></div>
                </div>
              </div>

              {/* Documents */}
              {selectedApp.documents && selectedApp.documents.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Documents</h3>
                  <div className="space-y-2">
                    {selectedApp.documents.map(doc => (
                      <button key={doc.id} onClick={() => openDoc(doc.id)} className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-2 w-full text-left transition-colors">
                        <FileText className="h-4 w-4 text-[#1565C0]" />
                        <span className="text-sm font-semibold capitalize">{(doc.doc_type || '').replace('_', ' ')}</span>
                        <ExternalLink className="h-3 w-3 text-gray-400 ml-auto" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* References */}
              {(selectedApp.reference_1_name || selectedApp.reference_2_name) && (
                <div data-testid="admin-app-references">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Reference Contacts</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[1, 2].map(n => (
                      <div key={n} className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid={`admin-app-reference-${n}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#1565C0] mb-1">Reference {n}</p>
                        <p className="text-sm font-semibold text-[#0A2540]">{selectedApp[`reference_${n}_name`] || '—'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {selectedApp[`reference_${n}_mobile`] ? `+91 ${selectedApp[`reference_${n}_mobile`]}` : ''}
                          {selectedApp[`reference_${n}_relation`] ? ` · ${selectedApp[`reference_${n}_relation`]}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedApp.status)}`}>
                  {selectedApp.status?.charAt(0).toUpperCase() + selectedApp.status?.slice(1)}
                </span>
              </div>

              {selectedApp.admin_remarks && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Previous Remarks</span>
                  <p className="text-sm mt-1">{selectedApp.admin_remarks}</p>
                </div>
              )}

              {/* Action Area */}
              {selectedApp.status === 'pending' && (
                <div className="border-t pt-6">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Review Application</h3>
                  {canApprove ? (
                    <>
                      <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Add remarks (optional)..." rows={3} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] mb-4" />
                      <div className="flex gap-3">
                        <Button onClick={() => handleReview('approve')} disabled={actionLoading} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold" data-testid="application-approve-btn">
                          {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-2" /> Approve</>}
                        </Button>
                        <Button onClick={() => handleReview('reject')} disabled={actionLoading} variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-xl py-3 font-semibold" data-testid="application-reject-btn">
                          {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-2" /> Reject</>}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900" data-testid="application-no-approve-perm">
                      <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">You can view this application but cannot approve or reject it.</div>
                        <div className="mt-0.5 text-amber-800">Ask your admin for the <code className="font-mono">approve_applications</code> permission.</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
